// NOM-035 — API Client (Fase 2: conectado a backend FastAPI + Firestore)

const API_BASE = '/api';

// Auth token getter — set by useAuth
let _getToken = null;
export function setTokenGetter(fn) { _getToken = fn; }

async function request(path, options = {}) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };

  // Add auth token if available
  if (_getToken) {
    try {
      const token = await _getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (e) { /* no token, continue without */ }
  }

  const res = await fetch(`${API_BASE}${path}`, { headers, ...options });
  if (res.status === 401) {
    throw new Error('No autorizado — inicia sesión');
  }
  if (res.status === 403) {
    const data = await res.json().catch(() => ({}));
    const detail = data.detail;
    if (detail && typeof detail === 'object' && detail.code) {
      // Structured plan limit error
      const err = new Error(detail.message);
      err.code = detail.code;
      err.detail = detail;
      throw err;
    }
    throw new Error(typeof detail === 'string' ? detail : 'No tienes acceso');
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

// ── Empresa ID persistence (keyed by user UID to prevent cross-account conflicts) ──

let _currentUid = null;
export function setCurrentUid(uid) { _currentUid = uid; }

function _key() {
  return _currentUid ? `nom035-empresa-${_currentUid}` : 'nom035-empresa-id';
}

export function getStoredEmpresaId() {
  if (_currentUid) {
    return localStorage.getItem(`nom035-empresa-${_currentUid}`) || null;
  }
  return localStorage.getItem('nom035-empresa-id');
}

export function setStoredEmpresaId(id) {
  // Clean old shared key if it exists
  if (_currentUid) localStorage.removeItem('nom035-empresa-id');
  localStorage.setItem(_key(), id);
}

export function clearStoredEmpresaId() {
  localStorage.removeItem(_key());
  localStorage.removeItem('nom035-empresa-id');
}

// ── Health check ──────────────────────────────────────────────

export async function checkHealth() {
  return request('/health');
}

// ── Full state load ───────────────────────────────────────────

export async function loadState(empresaId) {
  return request(`/state/${empresaId}`);
}

// ── Empresa ───────────────────────────────────────────────────

export async function createEmpresa(data) {
  return request('/empresa', { method: 'POST', body: JSON.stringify(data) });
}

export async function saveEmpresa(empresaId, data) {
  return request(`/empresa/${empresaId}`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Centros ───────────────────────────────────────────────────

export async function createCentro(empresaId, data) {
  return request(`/empresa/${empresaId}/centros`, { method: 'POST', body: JSON.stringify(data) });
}

export async function updateCentro(empresaId, centroId, data) {
  return request(`/empresa/${empresaId}/centros/${centroId}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function deleteCentro(empresaId, centroId) {
  return request(`/empresa/${empresaId}/centros/${centroId}`, { method: 'DELETE' });
}

// ── Trabajadores ──────────────────────────────────────────────

export async function addTrabajador(empresaId, centroId, data) {
  return request(`/empresa/${empresaId}/centros/${centroId}/trabajadores`, { method: 'POST', body: JSON.stringify(data) });
}

export async function importTrabajadores(empresaId, centroId, trabajadores) {
  return request(`/empresa/${empresaId}/centros/${centroId}/trabajadores/batch`, { method: 'POST', body: JSON.stringify({ trabajadores }) });
}

export async function deleteTrabajador(empresaId, centroId, trabId) {
  return request(`/empresa/${empresaId}/centros/${centroId}/trabajadores/${trabId}`, { method: 'DELETE' });
}

export async function updateTrabajador(empresaId, centroId, trabId, data) {
  return request(`/empresa/${empresaId}/centros/${centroId}/trabajadores/${trabId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// ── Fichas ────────────────────────────────────────────────────

export async function saveFicha(empresaId, centroId, trabId, data) {
  return request(`/empresa/${empresaId}/centros/${centroId}/fichas/${trabId}`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Consentimientos ───────────────────────────────────────────

export async function saveConsentimiento(empresaId, centroId, trabId, data) {
  return request(`/empresa/${empresaId}/centros/${centroId}/consentimientos/${trabId}`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Cuestionarios ─────────────────────────────────────────────

export async function saveCuestionario(empresaId, centroId, key, respuestas) {
  return request(`/empresa/${empresaId}/centros/${centroId}/cuestionarios/${key}`, { method: 'PUT', body: JSON.stringify({ respuestas }) });
}

// ── Política ──────────────────────────────────────────────────

export async function savePolitica(empresaId, data) {
  return request(`/empresa/${empresaId}/politica`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Programa ──────────────────────────────────────────────────

export async function savePrograma(empresaId, data, centroId) {
  if (centroId) {
    return request(`/empresa/${empresaId}/centros/${centroId}/programa`, { method: 'PUT', body: JSON.stringify(data) });
  }
  return request(`/empresa/${empresaId}/programa`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Settings ──────────────────────────────────────────────────

export async function saveSettings(empresaId, data) {
  return request(`/empresa/${empresaId}/settings`, { method: 'PUT', body: JSON.stringify(data) });
}

// ── Stripe / Suscripciones ────────────────────────────────────

export async function getPlans() {
  return request('/plans');
}

export async function createCheckout(planKey, successUrl, cancelUrl, extraCentros = 0) {
  return request('/checkout', { method: 'POST', body: JSON.stringify({ plan_key: planKey, success_url: successUrl, cancel_url: cancelUrl, extra_centros: extraCentros }) });
}

export async function verifyCheckout() {
  return request('/verify-checkout', { method: 'POST' });
}

export async function changePlan(planKey) {
  return request('/change-plan', { method: 'POST', body: JSON.stringify({ plan_key: planKey }) });
}

export async function comprarCentroAdicional(cantidad = 1) {
  return request('/comprar-centro-adicional', { method: 'POST', body: JSON.stringify({ cantidad }) });
}

export async function getSubscription() {
  return request('/subscription');
}

export async function getMisEmpresas() {
  return request('/mis-empresas');
}

// ── Portal del Trabajador ─────────────────────────────────────

export async function generarTokens(empresaId, centroId, trabajadorIds) {
  return request(`/empresa/${empresaId}/generar-tokens`, {
    method: 'POST',
    body: JSON.stringify({ empresa_id: empresaId, centro_id: centroId, trabajador_ids: trabajadorIds, base_url: window.location.origin }),
  });
}

export async function getTokens(empresaId) {
  return request(`/empresa/${empresaId}/tokens`);
}

// Public portal (no auth)
export async function getPortalInfo(token) {
  const res = await fetch(`${API_BASE}/portal/${token}`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const detail = data.detail;
    // Un link ya completado/rechazado se cierra (410 CLOSED) — no es un error
    // real, PortalPage usa este detalle para seguir mostrando la pantalla
    // final correcta en vez de un mensaje de error genérico.
    if (detail && typeof detail === 'object' && detail.code === 'CLOSED') {
      const err = new Error('closed');
      err.code = 'CLOSED';
      err.closedInfo = detail;
      throw err;
    }
    throw new Error((typeof detail === 'object' ? detail.message : detail) || `Error ${res.status}`);
  }
  return res.json();
}

// Los tres endpoints del portal pueden regresar `detail` como string (error
// simple) o como {code, message} (error estructurado, ej. STEP_MISMATCH) —
// se preserva `code` en el Error para que PortalPage decida cómo manejarlo.
async function portalRequest(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    const detail = d.detail;
    const isStructured = detail && typeof detail === 'object';
    const err = new Error(isStructured ? detail.message : (detail || `Error ${res.status}`));
    if (isStructured) err.code = detail.code;
    throw err;
  }
  return res.json();
}

export async function savePortalConsentimiento(token, data) {
  return portalRequest(`/portal/${token}/consentimiento`, data);
}

export async function savePortalFicha(token, data) {
  return portalRequest(`/portal/${token}/ficha`, data);
}

export async function savePortalCuestionario(token, guia, respuestas) {
  return portalRequest(`/portal/${token}/cuestionario/${guia}`, { respuestas });
}

export async function verifyPayment(sessionId) {
  return request('/verify-payment', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });
}

// ── Informe PDF (render Chromium headless en backend) ─────────

// No usa el helper `request()` genérico porque la respuesta es un PDF binario, no JSON.
// Marca local (por centro) de que ya se generó al menos un informe — solo para
// el checklist del Dashboard; la evidencia real de cumplimiento es el PDF en sí.
export function marcarInformeGenerado(centroId) {
  try { localStorage.setItem(`nom035-informe-generado-${centroId}`, '1'); } catch (e) { /* localStorage no disponible */ }
}
export function informeFueGenerado(centroId) {
  try { return localStorage.getItem(`nom035-informe-generado-${centroId}`) === '1'; } catch (e) { return false; }
}

export async function generarInformePDFServidor(html) {
  const headers = { 'Content-Type': 'application/json' };
  if (_getToken) {
    try {
      const token = await _getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch (e) { /* no token, continue without */ }
  }
  const res = await fetch(`${API_BASE}/informe/pdf`, {
    method: 'POST', headers, body: JSON.stringify({ html }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Error generando PDF (${res.status}): ${text || res.statusText}`);
  }
  return res.blob();
}
